/**
 * [BLURY] | 🥭 GrainFieldRenderer
 * Owns the offscreen buffers and composites a frame: shape layer → gaussian
 * blur → grain. Framework-free on purpose — React only tells it what to draw.
 * @build 13.09.26
 * @updated 13.09.26 PHT
 * @author TONYTONY Sàrl
 */
import { NoiseBuffer } from './noise';
import { drawShapeLayer, overscanFor } from './shapes';
import type { Composition, Size } from './types';

/**
 * Resolution-bound values — blur radius and grain tile size — are expressed in
 * preview pixels. Rendering the same composition at a larger output size has to
 * scale them, or a 4000 px export comes out sharper and finer-grained than the
 * preview it was tuned on. Shape radii are normalised already, so they follow
 * the canvas on their own.
 */
export function scaleComposition(composition: Composition, scale: number): Composition {
  if (scale === 1) return composition;
  return {
    ...composition,
    blur: composition.blur * scale,
    gsize: composition.gsize * scale,
  };
}

export class GrainFieldRenderer {
  private shapeCanvas = document.createElement('canvas');
  private shapeCtx: CanvasRenderingContext2D;
  private noise = new NoiseBuffer();

  constructor() {
    const ctx = this.shapeCanvas.getContext('2d');
    if (!ctx) throw new Error('GrainFieldRenderer: 2d context unavailable');
    this.shapeCtx = ctx;
  }

  /** Re-roll the grain on the next frame. */
  invalidateNoise(): void {
    this.noise.invalidate();
  }

  /**
   * Draw one frame into `target`. The target canvas is resized to `size` here,
   * which also clears it, so every call repaints the full frame.
   */
  render(target: HTMLCanvasElement, composition: Composition, size: Size, seed: number): void {
    const ctx = target.getContext('2d');
    if (!ctx) {
      console.warn('render: no 2d context on the target canvas, skipping');
      return;
    }

    if (target.width !== size.width || target.height !== size.height) {
      target.width = size.width;
      target.height = size.height;
      this.noise.invalidate(); // the grain tile is resolution-bound
    }

    const pad = overscanFor(composition.blur);
    this.shapeCanvas.width = size.width + pad * 2;
    this.shapeCanvas.height = size.height + pad * 2;
    drawShapeLayer(this.shapeCtx, composition, size, pad);

    ctx.save();
    ctx.clearRect(0, 0, size.width, size.height);
    ctx.filter = `blur(${composition.blur}px) saturate(${composition.sat}%) contrast(${composition.con}%)`;
    ctx.drawImage(this.shapeCanvas, -pad, -pad);
    ctx.restore();

    if (composition.grain > 0) {
      const tile = this.noise.get(composition, size, seed);
      ctx.save();
      ctx.globalCompositeOperation = composition.gblend;
      ctx.globalAlpha = composition.grain / 100;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(tile, 0, 0, size.width, size.height);
      ctx.restore();
    }
  }
}
